import PageForm from 'components/pageForm/PageForm';
import { SkeletonLine, SkeletonRows } from 'components/skeleton/Skeleton';
import LineTabs from 'components/tabs/LineTabs';

/**
 * The page of a record (a company, a group, a contact), as on the prototype:
 * on the left its identity, its actions and its information, which stay in
 * place on the whole height; on the right its numbers, its tabs and their
 * content, which scroll. Below 1024 px the two columns stack.
 */
const RecordLayout = ({ title, side, children }) => (
  <PageForm title={title}>
    {/* items-start: each column is as high as its content, not as the screen,
        or what sticks at the top of one would leave with it. */}
    <main className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 py-5 ls:flex-row ls:items-start lg:px-8">
      <aside className="flex w-full shrink-0 flex-col gap-4 ls:sticky ls:top-0 ls:h-full ls:w-[350px] ls:overflow-y-auto">
        {side}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col gap-5">{children}</div>
    </main>
  </PageForm>
);

// The tabs of a record, stuck at the top of the right column: the numbers
// leave with the page, the tabs stay.
export const RecordTabs = ({ tabs, activeTab, onChange }) => (
  <div className="sticky -top-5 z-20 -mt-5 bg-white pt-5">
    <LineTabs tabs={tabs} activeTab={activeTab} onChange={onChange} />
  </div>
);

// A card of a record: a title, what acts on it, and its content.
export const RecordSection = ({ title, action, className = '', children }) => (
  <section className={`rounded-xl border border-gray-100 p-4 ${className}`}>
    {(title || action) && (
      <div className="mb-2 flex items-center justify-between gap-3">
        {title && <p className="text-sm font-medium text-gray-900">{title}</p>}
        {action}
      </div>
    )}
    {children}
  </section>
);

const ghostCount = (count) => Array.from({ length: count }, (_, index) => index);

/**
 * A record to come, in the shapes of the loaded page so nothing jumps when it
 * lands: its identity and quick actions, its information, then `kpis` number
 * tiles (as KpiTiles' loading ones), `tabs` tabs and the rows of a card.
 */
export const RecordLayoutSkeleton = ({ kpis = 3, tabs = 4 }) => (
  <RecordLayout
    side={
      <>
        <section aria-busy="true" className="rounded-xl border border-gray-100 p-4">
          <SkeletonLine className="mb-4 h-8 w-28" />
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="h-12 w-12 shrink-0 animate-pulse rounded-lg bg-gray-200" />
            <div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
              <SkeletonLine className="h-5 w-40" />
              <SkeletonLine className="h-3 w-24" />
            </div>
          </div>
          <div className="mt-4 flex justify-between px-4">
            {ghostCount(5).map((index) => (
              <div key={index} className="flex flex-col items-center gap-1.5">
                <span aria-hidden="true" className="h-8 w-8 animate-pulse rounded-full bg-gray-200" />
                <SkeletonLine className="h-3 w-10" />
              </div>
            ))}
          </div>
        </section>
        <RecordSection>
          <div aria-busy="true">
            {ghostCount(6).map((index) => (
              <div key={index} className="flex flex-col gap-1.5 border-b border-gray-100 py-2.5 last:border-0">
                <SkeletonLine className="h-3 w-20" />
                <SkeletonLine className={index % 2 ? 'w-28' : 'w-36'} />
              </div>
            ))}
          </div>
        </RecordSection>
      </>
    }
  >
    {kpis > 0 && (
      <div aria-busy="true" className="grid grid-cols-1 gap-3 tab:grid-cols-3">
        {ghostCount(kpis).map((index) => (
          <div key={index} className="rounded-xl border border-gray-100 p-4">
            <SkeletonLine className="h-3.5 w-28" />
            <div className="mt-1 flex h-8 items-center">
              <SkeletonLine className="h-6 w-20" />
            </div>
            <div className="mt-1 flex h-4 items-center">
              <SkeletonLine className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    )}
    <div className="flex gap-5 border-b border-gray-100 pb-3">
      {ghostCount(tabs).map((index) => (
        <SkeletonLine key={index} className="w-16" />
      ))}
    </div>
    <section className="rounded-xl border border-gray-100">
      <SkeletonRows rows={4} />
    </section>
  </RecordLayout>
);

export default RecordLayout;
